"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { MessageSquare, ChevronUp, LogOut, ExternalLink, Search, Plus, Users, ImagePlus, Film, PenTool } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrgShares } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrgSearchDialog } from "./org-search-dialog";
import { OrganizationSwitcher } from "./organization-switcher";
import type { Organization } from "@/lib/types";
interface OrgSidebarProps {
  organizationId: string;
  currentShareId?: string;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (org: Organization | null) => void;
  className?: string;
}
interface OrgShareItem {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}
type OrgShareHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "share";
  key: string;
  share: OrgShareItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 32;
const ROW_HEIGHT_SPACER = 16;
const ROW_HEIGHT_SHARE = 60;
interface OrgShareHistoryRowProps {
  rows: OrgShareHistoryRow[];
  currentShareId?: string;
  onShareSelect: (shareId: string) => void;
}
function getOrgShareRowHeight(index: number, {
  rows
}: OrgShareHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_SHARE;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupSharesByDate(shares: OrgShareItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as OrgShareItem[],
    yesterday: [] as OrgShareItem[],
    lastWeek: [] as OrgShareItem[],
    older: [] as OrgShareItem[]
  };
  shares.forEach(share => {
    const shareDate = new Date(share.updatedAt);
    if (shareDate.toDateString() === today.toDateString()) {
      groups.today.push(share);
    } else if (shareDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(share);
    } else if (shareDate >= lastWeek) {
      groups.lastWeek.push(share);
    } else {
      groups.older.push(share);
    }
  });
  return groups;
}
function OrgShareRowComponent(t0) {
  const $ = _c(29);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentShareId,
    onShareSelect
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    share
  } = row;
  const t1 = currentShareId === share.id;
  let t2;
  if ($[8] !== onShareSelect || $[9] !== share.id) {
    t2 = () => onShareSelect(share.id);
    $[8] = onShareSelect;
    $[9] = share.id;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== share.title) {
    t4 = <div className="truncate text-sm font-medium mb-0.5">{share.title}</div>;
    $[12] = share.title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== share.updatedAt) {
    t5 = formatDate(share.updatedAt);
    $[14] = share.updatedAt;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <div className="text-xs text-muted-foreground">{t5}</div>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div className="flex-1 min-w-0">{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t7) {
    t8 = <div className="relative h-full px-2 pb-1"><div className="relative h-full"><SidebarMenuButton isActive={t1} onClick={t2} className="h-full! w-full justify-start gap-3 group relative" type="button">{t3}{t7}</SidebarMenuButton></div></div>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== ariaAttributes || $[26] !== style || $[27] !== t8) {
    t9 = <div {...ariaAttributes} style={style}>{t8}</div>;
    $[25] = ariaAttributes;
    $[26] = style;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
export function OrgSidebar(t0) {
  const $ = _c(121);
  const {
    organizationId,
    currentShareId,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className
  } = t0;
  const listContainerRef = useRef(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    data: orgSharesData,
    isLoading: isSharesLoading
  } = useOrgShares(organizationId);
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t1;
  if ($[0] !== currentTheme || $[1] !== setTheme) {
    t1 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[0] = currentTheme;
    $[1] = setTheme;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleTheme = t1;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  orgSharesData?.shares;
  let t2;
  if ($[3] !== orgSharesData?.shares) {
    t2 = orgSharesData?.shares ?? [];
    $[3] = orgSharesData?.shares;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shares = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(window.navigator.platform));
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isMac) {
    t5 = () => {
      const handleKeyDown = event => {
        const key = event.key.toLowerCase();
        const isSearchShortcut = isMac ? event.metaKey && key === "k" && !event.altKey && !event.ctrlKey : event.altKey && key === "k" && !event.metaKey && !event.ctrlKey;
        if (!isSearchShortcut || event.shiftKey || event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        setIsSearchOpen(true);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [isMac];
    $[7] = isMac;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== posthog || $[11] !== queryClient || $[12] !== router || $[13] !== signOut) {
    t7 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://llm-playground.agenzo.com/login");
          }
        }
      });
    };
    $[10] = posthog;
    $[11] = queryClient;
    $[12] = router;
    $[13] = signOut;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const logout = t7;
  let t8;
  if ($[15] !== organizationId || $[16] !== router) {
    t8 = shareId => {
      router.push(`/org/${organizationId}/chat/${shareId}`);
    };
    $[15] = organizationId;
    $[16] = router;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleShareSelect = t8;
  let t9;
  if ($[18] !== shares) {
    t9 = groupSharesByDate([...shares].sort(_temp));
    $[18] = shares;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const shareGroups = t9;
  let rows;
  if ($[20] !== shareGroups.lastWeek || $[21] !== shareGroups.older || $[22] !== shareGroups.today || $[23] !== shareGroups.yesterday) {
    const groups = [{
      title: "Today",
      shares: shareGroups.today
    }, {
      title: "Yesterday",
      shares: shareGroups.yesterday
    }, {
      title: "Last 7 days",
      shares: shareGroups.lastWeek
    }, {
      title: "Older",
      shares: shareGroups.older
    }];
    rows = [];
    groups.forEach((t10, groupIndex) => {
      const {
        title,
        shares: groupedShares
      } = t10;
      if (groupedShares.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedShares.forEach(share => {
        rows.push({
          type: "share",
          key: `share-${share.id}`,
          share
        });
      });
      const hasNextGroupWithShares = groups.slice(groupIndex + 1).some(_temp2);
      if (hasNextGroupWithShares) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[20] = shareGroups.lastWeek;
    $[21] = shareGroups.older;
    $[22] = shareGroups.today;
    $[23] = shareGroups.yesterday;
    $[24] = rows;
  } else {
    rows = $[24];
  }
  const historyRows = rows;
  let t10;
  if ($[25] !== currentShareId || $[26] !== handleShareSelect || $[27] !== historyRows) {
    t10 = {
      rows: historyRows,
      currentShareId,
      onShareSelect: handleShareSelect
    };
    $[25] = currentShareId;
    $[26] = handleShareSelect;
    $[27] = historyRows;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const rowProps = t10;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t11;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  if (!user) {
    let t11;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="size-6" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Chat</Badge></Link>;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t12;
    let t13;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="font-medium mb-2">Sign in required</div>;
      t13 = <p className="text-muted-foreground mb-3">Please sign in to view organizations and shared chats.</p>;
      $[31] = t12;
      $[32] = t13;
    } else {
      t12 = $[31];
      t13 = $[32];
    }
    let t14;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t11}<div className="w-full rounded-md border p-4 text-sm">{t12}{t13}<div className="flex items-center justify-end gap-2">{t14}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div></SidebarHeader>;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== className) {
      t16 = <Sidebar className={className}>{t15}</Sidebar>;
      $[35] = className;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    return t16;
  }
  if (isSharesLoading || isOrgLoading) {
    let t11;
    if ($[37] !== isOrgLoading || $[38] !== selectedOrganization) {
      t11 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[37] = isOrgLoading;
      $[38] = selectedOrganization;
      $[39] = t11;
    } else {
      t11 = $[39];
    }
    return t11;
  }
  let t11;
  if ($[40] !== className) {
    t11 = cn(className, "max-md:hidden");
    $[40] = className;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="LLM Gateway"><Link href="/" prefetch={true}><div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div><span className="text-lg font-bold tracking-tight">LLM Gateway</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="New Chat" className="border border-border"><Link href="/"><Plus className="h-4 w-4" /><span>New Chat</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Chat"><Link href="/" prefetch={true}><MessageSquare className="h-4 w-4" /><span>Chat</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Group Chat"><Link href="/group" prefetch={true}><Users className="h-4 w-4" /><span>Group Chat</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Image Studio"><Link href="/image" prefetch={true}><ImagePlus className="h-4 w-4" /><span>Image Studio</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Video Studio"><Link href="/video" prefetch={true}><Film className="h-4 w-4" /><span>Video Studio</span></Link></SidebarMenuButton></SidebarMenuItem>;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SidebarHeader><SidebarMenu>{t12}{t13}{t14}{t15}{t16}{t17}<SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Canvas"><Link href="/canvas" prefetch={true}><PenTool className="h-4 w-4" /><span>Canvas</span></Link></SidebarMenuButton></SidebarMenuItem></SidebarMenu></SidebarHeader>;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== onSelectOrganization || $[51] !== organizations || $[52] !== selectedOrganization) {
    t20 = organizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[50] = onSelectOrganization;
    $[51] = organizations;
    $[52] = selectedOrganization;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  let t22;
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsSearchOpen(true);
    t22 = <Search className="h-4 w-4" />;
    t23 = <span>Search Chats</span>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t21 = $[54];
    t22 = $[55];
    t23 = $[56];
  }
  const t24 = isMac ? "\u2318K" : "Alt+K";
  let t25;
  if ($[57] !== t24) {
    t25 = <SidebarMenu className="px-2"><SidebarMenuItem><SidebarMenuButton type="button" tooltip="Search Chats" onClick={t21}>{t22}{t23}<kbd className="ml-auto text-xs font-medium text-muted-foreground opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 group-data-[collapsible=icon]:hidden">{t24}</kbd></SidebarMenuButton></SidebarMenuItem></SidebarMenu>;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t20 || $[60] !== t25) {
    t26 = <div>{t19}{t20}{t25}</div>;
    $[59] = t20;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== historyRows || $[63] !== rowProps || $[64] !== shares.length) {
    t27 = shares.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No shared chats</p><p className="text-xs text-muted-foreground">Share a chat with this organization to see it here</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={OrgShareRowComponent} rowCount={historyRows.length} rowHeight={getOrgShareRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[62] = historyRows;
    $[63] = rowProps;
    $[64] = shares.length;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== isHistoryHidden || $[67] !== t27) {
    t28 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t27}</div>;
    $[66] = isHistoryHidden;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t26 || $[70] !== t28) {
    t29 = <SidebarContent className="overflow-hidden pb-2">{t26}{t28}</SidebarContent>;
    $[69] = t26;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== isOrgLoading || $[73] !== selectedOrganization) {
    t30 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={isOrgLoading} /></div>;
    $[72] = isOrgLoading;
    $[73] = selectedOrganization;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  const t31 = user?.name ?? "User";
  let t32;
  if ($[75] !== user?.name) {
    t32 = user?.name?.split(" ").map(_temp3).join("").toUpperCase().slice(0, 2) ?? "U";
    $[75] = user?.name;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t32) {
    t33 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t32}</span></div>;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  const t34 = user?.name;
  let t35;
  if ($[79] !== t34) {
    t35 = <span className="truncate font-semibold">{t34}</span>;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = user?.email;
  let t37;
  if ($[81] !== t36) {
    t37 = <span className="truncate text-xs text-muted-foreground">{t36}</span>;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== t35 || $[84] !== t37) {
    t38 = <div className="grid flex-1 text-left text-sm leading-tight">{t35}{t37}</div>;
    $[83] = t35;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t31 || $[88] !== t33 || $[89] !== t38) {
    t40 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t31} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t33}{t38}{t39}</SidebarMenuButton></DropdownMenuTrigger>;
    $[87] = t31;
    $[88] = t33;
    $[89] = t38;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llm-gateway.agenzo.com/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t42 = <DropdownMenuSeparator />;
    $[91] = t41;
    $[92] = t42;
  } else {
    t41 = $[91];
    t42 = $[92];
  }
  let t43;
  if ($[93] !== toggleTheme) {
    t43 = event_0 => {
      event_0.preventDefault();
      toggleTheme();
    };
    $[93] = toggleTheme;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span>Theme</span>;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== t43) {
    t46 = <DropdownMenuItem className="justify-between gap-3" onSelect={t43}>{t44}{t45}</DropdownMenuItem>;
    $[97] = t43;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <DropdownMenuSeparator />;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <LogOut className="mr-2 h-4 w-4" />;
    $[100] = t48;
  } else {
    t48 = $[100];
  }
  let t49;
  if ($[101] !== logout) {
    t49 = <DropdownMenuItem onClick={logout}>{t48}Log out</DropdownMenuItem>;
    $[101] = logout;
    $[102] = t49;
  } else {
    t49 = $[102];
  }
  let t50;
  if ($[103] !== t46 || $[104] !== t49) {
    t50 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t41}{t42}{t46}{t47}{t49}</DropdownMenuContent>;
    $[103] = t46;
    $[104] = t49;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] !== t40 || $[107] !== t50) {
    t51 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t40}{t50}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[106] = t40;
    $[107] = t50;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] !== t30 || $[110] !== t51) {
    t52 = <SidebarFooter>{t30}{t51}</SidebarFooter>;
    $[109] = t30;
    $[110] = t51;
    $[111] = t52;
  } else {
    t52 = $[111];
  }
  let t53;
  if ($[112] !== handleShareSelect || $[113] !== isSearchOpen || $[114] !== organizationId) {
    t53 = <OrgSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} organizationId={organizationId} onShareSelect={handleShareSelect} />;
    $[112] = handleShareSelect;
    $[113] = isSearchOpen;
    $[114] = organizationId;
    $[115] = t53;
  } else {
    t53 = $[115];
  }
  let t54;
  if ($[116] !== t11 || $[117] !== t29 || $[118] !== t52 || $[119] !== t53) {
    t54 = <Sidebar collapsible="icon" className={t11}>{t18}{t29}{t52}{t53}</Sidebar>;
    $[116] = t11;
    $[117] = t29;
    $[118] = t52;
    $[119] = t53;
    $[120] = t54;
  } else {
    t54 = $[120];
  }
  return t54;
}
function _temp5(event_2) {
  return event_2.stopPropagation();
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(n) {
  return n[0];
}
function _temp2(group) {
  return group.shares.length > 0;
}
function _temp(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}